import { getConfigForImportedTest, getConfigForMeasuredTest, getConfigForTest } from 'Components/Worksheets/Config';
import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import {
  wrapAsDerivedFromMeasurementsConfig as derivedFrom,
  measuredUsingConfigFor as measuredUsing,
  mergeConfig,
} from 'Components/Worksheets/Core/Helpers/configHelpers';
import { getMoistureContentPercent } from 'Components/Worksheets/MoistureContentWorksheet/BsEnIso/Config/index';
import { getSeparationPath } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/Helpers';
import { getDerivedFromMeasurementsConfigForPortionSieving } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/derivedFromMeasurementsConfig';
import { getMeasuredUsingConfigForPortionSieving } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/measuredUsingConfig';
import { DryMassSource, WetMassSource } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/types';
import qestIds from 'Components/Worksheets/QestIds';
import * as oPath from 'Lib/Object/Path/Immutable';
import getLabelConfig from './labelConfig';

/**
 * Most masses are formatted based on the readability of the corresponding balance.
 * However, some require more complicated logic to determine which balances apply.
 * For these masses, we're currently keeping it simple and displaying them to a fixed 0.01 g
 * (default readability).
 */
const massConfig = { uom: 'g', format: { nearest: 0.01 } };

const getConfig = (prefix: string) => {
  const maxSeparations = 2;

  let config = {
    ...getPortionMassesConfig(`${prefix}.WholePortion.Masses`, 'Portion'),
    ...getPortionSievingConfig(prefix, `${prefix}.WholePortion.Sieving`),
    ...getPortionWashingConfig(`${prefix}.WholePortion.Washing`),
    ...getPortionPretreatmentConfig(`${prefix}.WholePortion`),
    [`${prefix}.Preparation.AddSieveSizesBasedOnSpecification`]: {
      options: [
        { value: false, label: 'Based on the selected sieve set' },
        { value: true, label: `Based on the sample's specification` },
      ],
    },
    [`${prefix}.Results.D85`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D60`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D50`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D30`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D15`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D10`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.CoefficientOfCurvature`]: { format: { nearest: 0.01 } },
    [`${prefix}.Results.CoefficientOfUniformity`]: { format: { nearest: 0.01 } },
    [`${prefix}.ImagesAndRemarks.Remarks`]: { maxLength: 255 },
    [`${prefix}.ImagesAndRemarks.InternalRemarks`]: { maxLength: 255 },
    [`${prefix}.Results.PercentagePassingWashingSieve`]: { format: { nearest: 0.1 } },
    [`${prefix}.Preparation.TestStandardAndMethod`]: {
      options: [
        { label: 'BS 1377-2 Clause 10', value: 'Bs1377_2_Clause10_2022' },
        { label: 'BS EN ISO 17892-4 Clause 5.2', value: 'BsEnIso17892_4_Clause5_2_2016' },
      ],
    },
  };

  Object.assign(
    config,
    ...[...Array(maxSeparations).keys()].map((i) => ({
      ...getPortionMassesConfig(`${prefix}.${getSeparationPath(i)}.CoarsePortion.Masses`, 'Coarse Portion'),
      ...getPortionSievingConfig(prefix, `${prefix}.${getSeparationPath(i)}.CoarsePortion.Sieving`),
      ...getPortionMassesConfig(`${prefix}.${getSeparationPath(i)}.FinePortion.Masses`, 'Fine Portion'),
      //For this test method, the "fine portion moisture content" is attached to the fine portion subspecimen,
      //but it is actually a *different* subspecimen. We avoid the 'Subspecimen' prefix because we don't want
      //users to be confused by it.
      ...getPortionMassesConfig(`${prefix}.${getSeparationPath(i)}.FinePortion.Subspecimen.Masses`, 'Material'),
      ...getPortionSievingConfig(prefix, `${prefix}.${getSeparationPath(i)}.FinePortion.Subspecimen.Sieving`),
      ...getPortionWashingConfig(`${prefix}.${getSeparationPath(i)}.FinePortion.Subspecimen.Washing`),
    }))
  );

  return mergeConfig(config, getLabelConfig(prefix));
};

const getPortionMassesConfig = (prefix: string, materialLabel: string) => ({
  [`${prefix}.WetMass`]: (configArgs: IConfigFunctionArgs) => {
    const wetMassSource = oPath.get(configArgs.worksheet, `${prefix}.WetMassSource`) as WetMassSource;
    switch (wetMassSource) {
      case WetMassSource.Measured:
        return derivedFrom([`${prefix}.MassMeasurement.TareMass`, `${prefix}.MassMeasurement.TareAndWetMass`]);
      default:
        return massConfig;
    }
  },
  ...getConfigForMeasuredTest(
    qestIds.moistureContent.BS_EN_ISO_17892_1.rev2014,
    `${prefix}.AssociatedMoistureContent`,
    {
      materialLabelOverride: materialLabel,
    }
  ),

  [`${prefix}.DryMass`]: (configArgs: IConfigFunctionArgs) => {
    const dryMassSource = oPath.get(configArgs.worksheet, `${prefix}.DryMassSource`) as DryMassSource;
    switch (dryMassSource) {
      case DryMassSource.Measured:
        return derivedFrom([`${prefix}.MassMeasurement.TareMass`, `${prefix}.MassMeasurement.TareAndDryMass`]);
      case DryMassSource.CalculatedFromMoistureContentAndWetMass:
        return derivedFrom([`${prefix}.MassMeasurement.TareMass`, `${prefix}.MassMeasurement.TareAndWetMass`]);
      default:
        return massConfig;
    }
  },

  [`${prefix}.MassUpscaleFactor`]: { format: { nearest: 0.01 } },
  ...getPortionMassMeasurementConfig(prefix),
  ...getConfigForImportedTest(qestIds.moistureContent.BS_EN_ISO_17892_1.rev2014, `${prefix}.AssociatedMoistureContent`),
  ...getMoistureContentPercent(`${prefix}.AssociatedMoistureContent.ExternalTest.Results.MoistureContentPercent`),
  ...getMoistureContentPercent(`${prefix}.MoistureContent`),
});

const getWetMassMeasurementsConfig = (prefix: string) => {
  const measuredUsingBalance = measuredUsing(`${prefix}.Equipment.Balance`);

  return {
    [`${prefix}.WetMassMeasurements.WetMass`]: derivedFrom([
      `${prefix}.WetMassMeasurements.TareMass`,
      `${prefix}.WetMassMeasurements.TareAndWetMass`,
    ]),
    [`${prefix}.WetMass`]: derivedFrom([
      `${prefix}.WetMassMeasurements.TareMass`,
      `${prefix}.WetMassMeasurements.TareAndWetMass`,
    ]),

    [`${prefix}.WetMassMeasurements.TareMass`]: measuredUsingBalance,
    [`${prefix}.WetMassMeasurements.TareAndWetMass`]: measuredUsingBalance,
  };
};

/**
 * @param portionPrefix prefix for the PsdPortion that is the parent of the Pretreatment model
 * @returns configuration for the Pretreatment of a PsdPortion
 */
const getPortionPretreatmentConfig = (portionPrefix: string) => {
  const prefix = `${portionPrefix}.Pretreatment`;
  const massMeasurementPrefix = `${portionPrefix}.Masses.MassMeasurement`;
  return mergeConfig({
    ...mergeConfig(
      getConfigForTest(qestIds.moistureContent.BS_EN_ISO_17892_1.rev2014, `${prefix}.MeasuredConstantDryMass`),
      {
        [`${prefix}.MeasuredConstantDryMass.Equipment.Balance`]: { defaultReadability: 0.01, defaultUom: 'g' },
        [`${prefix}.MeasuredConstantDryMass.Equipment.DryMassBalance`]: { defaultReadability: 0.01, defaultUom: 'g' },
      }
    ),
    [`${prefix}.UntreatedMaterialDryMass`]: derivedFrom([
      `${massMeasurementPrefix}.TareMass`,
      `${massMeasurementPrefix}.TareAndWetMass`,
    ]),
    [`${prefix}.Equipment.Balance`]: { defaultReadability: 0.01, defaultUom: 'g' },
    [`${prefix}.TareID`]: { maxLength: 20 },
    [`${prefix}.TareMass`]: measuredUsing(`${prefix}.Equipment.Balance`),
    [`${prefix}.TareAndPretreatedMaterialDryMass`]: measuredUsing(`${prefix}.Equipment.Balance`),
    [`${prefix}.PretreatedMaterialDryMass`]: derivedFrom([
      `${prefix}.MeasuredConstantDryMass.DryingTray.ContainerMass`,
      `${prefix}.MeasuredConstantDryMass.DryingTray.DryMassReadings.DrySoilAndContainerMass`,
    ]),
  });
};

const getPortionWashingConfig = (prefix: string) =>
  mergeConfig({
    //TODO - readability for the washed dry mass should come from associated wash test configuration.
    //I don't think we can actually chain together multiple levels of derivation, so we have to go
    //straight to the actual measured properties.
    //It's possible to implement it as:
    //   [`${prefix}.WashedDryMass`]: derivedFrom([
    //     `${prefix}.AssociatedWashTest.MeasuredTest.Masses.MeasuredWashedConstantDryMass.DryingTray.DryMassReadings.DrySoilAndContainerMass`,
    //   ]),
    //which matches the behaviour of the wash test, but it's very fragile.
    //The next best option is probably to have an exported function from the wash test config
    //similar to getWashConfig so that we can match the precision of a specific result field.
    [`${prefix}.WashedDryMass`]: massConfig,
    [`${prefix}.WashingsDryMass`]: massConfig,
    ...getConfigForMeasuredTest(qestIds.wash.BS_EN_ISO_17892_4_Clause_5_2.Rev2016, `${prefix}.AssociatedWashTest`),
  });

const getSieveConfig = (portionSievingPath: string) => (configArgs: any) => {
  const units = oPath.get(configArgs.worksheet, `${portionSievingPath}.Equipment.SieveSetUnits`);
  return {
    format: units === 'Metric' ? { nearest: 1 } : { nearest: 0.1 },
    uom: units === 'Metric' ? 'mm' : 'in',
  };
};

//Note that the prefix parameter should already contain the root prefix within, we just needed the root separately
const getPortionSievingConfig = (rootPrefix: string, portionSievingPath: string) =>
  mergeConfig(
    {
      [`${portionSievingPath}.Equipment.SieveSetDimensions.Width`]: getSieveConfig(portionSievingPath),
      [`${portionSievingPath}.Equipment.SieveSetDimensions.Length`]: getSieveConfig(portionSievingPath),
      [`${portionSievingPath}.Equipment.SieveSetDimensions.Diameter`]: getSieveConfig(portionSievingPath),
      [`${portionSievingPath}.RepresentedDryMass`]: massConfig,
      [`${portionSievingPath}.Records.PercentagePassing`]: { format: { nearest: 0.1 } },
      [`${portionSievingPath}.ExpectedIndividualPanMassRetained`]: massConfig,
    },
    getMeasuredUsingConfigForPortionSieving(portionSievingPath),
    getDerivedFromMeasurementsConfigForPortionSieving(portionSievingPath),
    {
      [`${portionSievingPath}.Equipment.Balance`]: { defaultReadability: 0.01, defaultUom: 'g' },
    }
  );

const getPortionMassMeasurementConfig = (portionPrefix: string) => {
  const prefix = `${portionPrefix}.MassMeasurement`;
  const dryMassOnly = (configArgs: IConfigFunctionArgs) => {
    const wetMassSource = oPath.get(configArgs.worksheet, `${portionPrefix}.WetMassSource`) as WetMassSource;
    const dryMassSource = oPath.get(configArgs.worksheet, `${portionPrefix}.DryMassSource`) as DryMassSource;
    return wetMassSource !== WetMassSource.Measured && dryMassSource === DryMassSource.Measured;
  };

  const measuredUsingBalance = measuredUsing(`${prefix}.Equipment.Balance`);
  const measuredUsingDryMassBalance = measuredUsing(`${prefix}.Equipment.DryMassBalance`);
  return {
    ...mergeConfig(
      getConfigForTest(qestIds.moistureContent.BS_EN_1097_5.rev2008, `${prefix}.MeasuredConstantDryMass`),
      {
        [`${prefix}.MeasuredConstantDryMass.Equipment.Balance`]: { defaultReadability: 0.01, defaultUom: 'g' },
        [`${prefix}.MeasuredConstantDryMass.Equipment.DryMassBalance`]: { defaultReadability: 0.01, defaultUom: 'g' },
      }
    ),

    ...getWetMassMeasurementsConfig(`${prefix}`),

    [`${prefix}.TareID`]: { maxLength: 20 },
    [`${prefix}.TareMass`]: (configArgs: IConfigFunctionArgs) =>
      dryMassOnly(configArgs) ? measuredUsingDryMassBalance : measuredUsingBalance,
    [`${prefix}.TareAndWetMass`]: measuredUsingBalance,
    [`${prefix}.TareAndDryMass`]: measuredUsingDryMassBalance,

    [`${prefix}.WetMass`]: derivedFrom([`${prefix}.TareMass`, `${prefix}.TareAndWetMass`]),
    [`${prefix}.DryMass`]: derivedFrom([`${prefix}.TareMass`, `${prefix}.TareAndDryMass`]),

    [`${prefix}.Equipment.Balance`]: { defaultReadability: 0.01, defaultUom: 'g' },
    [`${prefix}.Equipment.DryMassBalance`]: { defaultReadability: 0.01, defaultUom: 'g' },
  };
};

export { getConfig };

export default {
  [qestIds.psdSieveAnalysis.BS_EN_ISO_17892_4_Clause_5_2.Rev2016]: getConfig,
};
